import { Link } from "wouter";
import { useBookCta } from "@/hooks/use-book-href";
import { KUALOA_STAY_PATH } from "@shared/property-context";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { SectionHeading } from "@/components/ui/section-heading";
import {
  useSEO,
  useSEOSettings,
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateKnowledgeGraphSchema,
  generateSpeakableSchema,
  type SiteInfoSettings,
} from "@/lib/seo-utils";
import { ContactActionLinks } from "@/components/contact/contact-action-links";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import { CANCELLATION_POLICY_SUMMARY, BOOK_DIRECT_SAVINGS, STRIPE_SECURE_PAYMENT, SECURITY_DEPOSIT_SUMMARY } from "@shared/property-facts";
import { useQuery } from "@tanstack/react-query";
import { useSiteBrand } from "@/hooks/use-site-brand";

const faqItems = [
  {
    question: "Where is the vacation rental located?",
    answer:
      "Our beachfront property is in Ka'a'awa on Oahu's windward coast, directly across from Kualoa Ranch. It's about 23 miles (30 minutes) from Honolulu International Airport with stunning ocean views toward Mokoli'i (Chinaman's Hat).",
  },
  {
    question: "How do I book directly?",
    answer:
      `Select your dates on our booking page, enter guest count, and complete Stripe checkout. You receive instant email confirmation. ${BOOK_DIRECT_SAVINGS}`,
  },
  {
    question: "Is payment secure?",
    answer: STRIPE_SECURE_PAYMENT,
  },
  {
    question: "What is the cancellation policy?",
    answer:
      CANCELLATION_POLICY_SUMMARY,
  },
  {
    question: "Is there a security deposit?",
    answer: SECURITY_DEPOSIT_SUMMARY,
  },
  {
    question: "What are check-in and check-out times?",
    answer:
      "Standard check-in is 3:00 PM and check-out is 10:00 AM HST. Early check-in or late checkout may be available upon request depending on availability.",
  },
  {
    question: "How many guests can the property accommodate?",
    answer:
      "Our 3-bedroom oceanfront beach house comfortably sleeps up to 8 guests, ideal for families or small groups visiting Oahu.",
  },
  {
    question: "Is the property truly beachfront?",
    answer:
      "Yes. The home sits directly on the oceanfront in Ka'a'awa with private beach access, panoramic Pacific views, and calm waters ideal for families.",
  },
  {
    question: "What amenities are included?",
    answer:
      "Guests enjoy a full kitchen, high-speed WiFi, A/C, washer/dryer, beach gear, outdoor entertaining space, parking, and premium linens and towels.",
  },
  {
    question: "What is there to do near Ka'a'awa?",
    answer:
      "Walk to Kualoa Ranch for tours and film-site adventures, kayak to Mokoli'i (Chinaman's Hat), explore windward coast beaches, or day-trip to the North Shore and Polynesian Cultural Center.",
  },
  {
    question: "Do you charge cleaning fees?",
    answer:
      "A cleaning fee is disclosed during checkout before you confirm your reservation. All fees are shown transparently with no hidden charges at direct booking.",
  },
  {
    question: "How quickly do you respond to inquiries?",
    answer:
      "We respond to contact form messages and emails within 24 hours, often much sooner. For urgent booking questions, call, text, or email us using the contact options below.",
  },
];

export default function FaqPage() {
  const bookCta = useBookCta();
  const siteBrand = useSiteBrand();
  const { seoSettings } = useSEOSettings();
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ["/api/settings/site_info"],
    retry: false,
  });

  const seoHead = useSEO({
    pageType: "faq",
    canonical: "https://vacationrentaloahu.co/faq",
    structuredData: [
      generateKnowledgeGraphSchema({ seoSettings, siteInfoSettings: siteSettings }),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "FAQ", url: "/faq" },
      ]),
      generateFAQSchema(faqItems),
      generateSpeakableSchema(["h1", ".faq-answer"]),
    ],
  });

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
        <Header />
        <main id="main-content">
          <PageHero
            eyebrow="Help"
            title="Frequently Asked Questions"
            subtitle="Direct answers about booking, amenities, and staying at our Ka'a'awa beachfront vacation rental on Oahu."
            videoSrc={wavesVideo}
            posterSrc={wavesPoster}
            imageAlt="Ocean waves along the Ka'a'awa windward coast"
          />

          <section className="py-16 md:py-24 bg-shell">
            <div className="container mx-auto px-6 max-w-3xl">
              <Breadcrumbs
                items={[
                  { name: "Home", url: "/" },
                  { name: "FAQ", url: "/faq" },
                ]}
                className="mb-12 justify-center"
              />

              <p className="text-ink-soft leading-relaxed mb-10 text-center max-w-2xl mx-auto">
                {siteBrand.portfolio ? "Vacation Rental Oahu's homes are Ka'a'awa, Oahu beachfront rentals you book direct." : "The Kualoa Beach House is a Ka'a'awa, Oahu beachfront rental you book direct."} {BOOK_DIRECT_SAVINGS} {STRIPE_SECURE_PAYMENT}
              </p>

              <div className="space-y-0 border-t border-sand-deep">
                {faqItems.map((faq, idx) => (
                  <div key={faq.question} className="faq-item py-8 border-b border-sand-deep group">
                    <h2 className="text-luxury-xl font-serif text-forest mb-4 tracking-luxury-tight group-hover:text-palm transition-colors duration-300 flex gap-4">
                      <span className="text-driftwood font-sans font-light italic text-base mt-1">{String(idx + 1).padStart(2, '0')}</span>
                      {faq.question}
                    </h2>
                    <p className="faq-answer text-ink-soft leading-relaxed pl-9">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-20 md:py-28 bg-sand border-t border-sand-deep text-center">
            <div className="container mx-auto px-6 max-w-2xl">
              <SectionHeading
                eyebrow="Contact"
                title="Still have questions?"
                lede="If you could not find the answer you were looking for, reach out to our team."
              />
              <ContactActionLinks
                phone={siteSettings?.contact_phone}
                email={siteSettings?.contact_email}
                variant="pills"
                className="mt-8 justify-center"
                testIdPrefix="faq"
              />
              <div className="mt-8 flex flex-wrap justify-center items-center gap-3">
                <Link href={bookCta.href} className="inline-flex items-center justify-center whitespace-nowrap bg-sun text-shell hover:bg-sun-hover font-sans font-semibold uppercase text-[12px] tracking-[0.16em] px-6 py-3 rounded-lg transition-transform duration-300 hover:-translate-y-0.5">
                  {bookCta.choosing ? "Choose a Home" : "Check Availability"}
                </Link>
                <Link href={bookCta.choosing ? "/properties" : KUALOA_STAY_PATH} className="inline-flex items-center justify-center whitespace-nowrap bg-transparent border border-ink text-ink hover:bg-ink hover:text-shell font-sans font-semibold uppercase text-[12px] tracking-[0.16em] px-6 py-3 rounded-lg transition-all duration-300">
                  {bookCta.choosing ? "View Our Homes" : "View Property Details"}
                </Link>
                <Link href="/contact" className="inline-flex items-center justify-center whitespace-nowrap text-ink hover:text-ink-soft hover:underline font-sans font-semibold uppercase text-[12px] tracking-[0.16em] px-4 py-3 transition-colors duration-300">
                  Contact Us
                </Link>
              </div>
            </div>
          </section>
        </main>
        <StickyMobileBookCta />
      <Footer />
      </div>
    </>
  );
}
